import { FormFieldConfig } from '../../../../shared/components/dynamic-form/form-field.model';

/** The feature whose field configuration drives this screen. */
export const FAMILY_PROFILE_FEATURE = 'FamilyProfile';

/** The page header already shows this field, so the read-only cards skip it. */
export const HEADER_FIELD = 'familyName';

/** One group of fields: a divider in the edit panel, a card in the read-only view. */
export interface ProfileSection {
  key: string;
  /** Read-only card this section's fields land under. Sections sharing a title merge into one card. */
  card: string;
  /** Divider above this section in the edit panel. */
  dividerBefore?: boolean;
  /** Hidden until the user asks for it — the second guardian. */
  optional?: boolean;
  fields: FormFieldConfig[];
}

/** Layout of the profile screen, in render order.
 *
 *  Presentation only: labels, order, types, validation and whether a field
 *  exists at all come from the field-configuration API. This table just says
 *  which card each key is drawn under, because the API has no section metadata
 *  yet (the spec adds `sections` to GetFormSchema). A key the API adds that
 *  isn't listed here still renders — it falls into the last section rather than
 *  disappearing — and a key listed here that the API drops leaves no trace. */
interface SectionLayout extends Omit<ProfileSection, 'fields'> {
  /** Field keys this section claims. The catch-all section claims the rest. */
  keys?: string[];
}

const SECTION_LAYOUT: SectionLayout[] = [
  { key: 'contact', card: 'Family Details', keys: [HEADER_FIELD, 'email'] },
  { key: 'address', card: 'Address', keys: ['address', 'city'] },
  { key: 'location', card: 'Address', keys: ['state', 'zipCode'] },
  { key: 'secondary', card: 'Family Details', keys: ['secondaryFamilyEmailAddress', 'phone'] },
  { key: 'guardian1', card: 'Guardian', dividerBefore: true, keys: ['guardian1Name', 'guardian1Phone'] },
  { key: 'guardian2', card: 'Guardian', optional: true, keys: ['guardian2Name', 'guardian2Phone'] },
  {
    key: 'emergency',
    card: 'Emergency Contact',
    dividerBefore: true,
    keys: ['emergencyContactName', 'emergencyPhoneNumber', 'relationshipToFamilyMember']
  },
  // Catch-all: claims every key the layout above doesn't name.
  { key: 'other', card: 'Other Details', dividerBefore: true }
];

/** Groups configured fields into the screen's sections, keeping the API's field
 *  order inside each one. Empty sections are dropped, so the cards a family
 *  sees follow whatever the API actually configured. */
export function groupProfileFields(fields: FormFieldConfig[]): ProfileSection[] {
  const catchAll = SECTION_LAYOUT.find((section) => !section.keys);
  // Case-insensitive, so an API key recasing doesn't drop a field into the catch-all.
  const claims = (section: SectionLayout, key: string) =>
    !!section.keys?.some((k) => k.toLowerCase() === key.toLowerCase());

  const sectionOf = (field: FormFieldConfig): SectionLayout | undefined =>
    SECTION_LAYOUT.find((section) => claims(section, field.key)) ?? catchAll;

  return SECTION_LAYOUT.reduce<ProfileSection[]>((sections, layout) => {
    const sectionFields = fields.filter((field) => sectionOf(field)?.key === layout.key);
    if (sectionFields.length) {
      sections.push({
        key: layout.key,
        card: layout.card,
        dividerBefore: layout.dividerBefore,
        optional: layout.optional,
        fields: sectionFields
      });
    }
    return sections;
  }, []);
}
